import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type ToggleIconDirectionConstantType,
    type ToggleColorsConstantType,
    type ToggleDynamicStyleType,
    type ToggleSizeConstantType,
    type ToggleIconDirection,
    type ToggleMeasures,
    type ToggleVariant,
    type ToggleColors
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    type ElevationLevelType,
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getControlMeasures,
    getHitArea
} from "../../helpers/theme/control";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const TOGGLE_ICON_DIRECTIONS: Record<ToggleIconDirection, ToggleIconDirection> = {
    start: "start",
    end: "end"
};

export const TOGGLE_VARIANT_ELEVATIONS: Record<ToggleVariant, ElevationLevelType> = {
    outline: "level1",
    ghost: "level0"
};

export const getToggleSize = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    iconSizes,
    size
}: ToggleSizeConstantType): ToggleMeasures => {
    return getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });
};

export const getToggleIconDirection = ({
    iconDirection
}: ToggleIconDirectionConstantType): ToggleIconDirection => {
    return TOGGLE_ICON_DIRECTIONS[iconDirection];
};

export const getToggleColors = ({
    isHighlighted,
    isDisabled,
    isActive,
    variant,
    colors
}: ToggleColorsConstantType): ToggleColors => {
    if(isDisabled) {
        const disabledColors = getTypeColors({
            variant: isActive ? "tonal" : variant,
            isDisabled: true,
            type: "neutral",
            colors
        });

        const hasBorder = isActive || variant === "outline";

        return {
            ...disabledColors,
            borderColor: hasBorder ? getTypeColors({
                variant: "outline",
                isDisabled: true,
                type: "neutral",
                colors
            }).borderColor : disabledColors.borderColor,
            iconColorKey: "disabled"
        };
    }

    const baseColors = getTypeColors({
        isSelected: isActive,
        type: "neutral",
        variant,
        colors
    });

    if(isActive || isHighlighted) {
        return {
            ...baseColors,
            overlayColor: "default",
            iconColorKey: "high"
        };
    }

    return {
        ...baseColors,
        titleColor: colors.content.text.mid,
        iconColor: colors.content.icon.mid,
        overlayColor: "default",
        iconColorKey: "mid"
    };
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center"
    },
    content: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1
    },
    activeIconContainer: {
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        bottom: 0,
        start: 0,
        top: 0,
        end: 0
    },
    startAlign: {
        alignItems: "flex-start"
    },
    stretchContainer: {
        alignSelf: "stretch",
        alignItems: "stretch"
    },
    title: {
        textAlign: "center",
        flexShrink: 1
    },
    fillSurface: {
        flexGrow: 1
    }
});

export const useStyles = ({
    isFocusVisible,
    currentColors,
    borderRadius,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    shadowAlpha,
    elevations,
    isDisabled,
    isIconOnly,
    isActive,
    borders,
    variant,
    colors,
    spaces
}: ToggleDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: currentSize.minHeight,
        touchTargets,
        pointerType
    });

    const currentElevation = TOGGLE_VARIANT_ELEVATIONS[variant];

    const styles = {
        surface: {
            paddingHorizontal: currentSize.paddingHorizontal,
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            minHeight: currentSize.minHeight,
            minWidth: currentSize.minHeight,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        container: {
            minHeight: hitArea.minHeight,
            minWidth: hitArea.minWidth
        } as Mutable<ViewStyle>,
        content: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(!isDisabled && !isActive && elevations[currentElevation].length) {
        Object.assign(styles.surface, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: currentElevation,
            shadowAlpha,
            elevations
        }));
    }

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: "default",
        isFocusVisible,
        borders,
        colors
    }));

    if(isIconOnly) {
        delete styles.surface.paddingHorizontal;
    }

    return styles;
};
export default stylesheet;
